export const FRESHNESS_STAMP_STATUSES = ['current', 'partial', 'blocked'] as const;

export type FreshnessStampStatus = (typeof FRESHNESS_STAMP_STATUSES)[number];

export type FreshnessStampSectionPropsV1 = Readonly<{
  id: string;
  title: string;
  asOf: string | null;
  status: FreshnessStampStatus;
  maxAgeHours: number;
}>;

export type FreshnessStampPropsV1 = Readonly<{
  asOf: string | null;
  label?: string;
  status?: FreshnessStampStatus;
  sections?: readonly FreshnessStampSectionPropsV1[];
}>;

const FRESHNESS_STAMP_PROP_KEYS = new Set(['asOf', 'label', 'status', 'sections']);
const FRESHNESS_STAMP_SECTION_KEYS = ['id', 'title', 'asOf', 'status', 'maxAgeHours'];
const FRESHNESS_STAMP_STATUS_SET = new Set<string>(FRESHNESS_STAMP_STATUSES);
const FRESHNESS_STAMP_PUBLIC_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/u;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: ReadonlySet<string>,
): boolean {
  return Object.keys(value).every((key) => allowedKeys.has(key));
}

function hasExactKeys(
  value: Record<string, unknown>,
  expectedKeys: readonly string[],
): boolean {
  const actualKeys = Object.keys(value).sort();
  const expected = [...expectedKeys].sort();
  return (
    actualKeys.length === expected.length &&
    actualKeys.every((key, index) => key === expected[index])
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNullablePublicTimestamp(value: unknown): value is string | null {
  if (value === null) return true;
  if (typeof value !== 'string' || !FRESHNESS_STAMP_PUBLIC_TIMESTAMP.test(value)) {
    return false;
  }

  const parsed = new Date(value);
  const canonicalValue = value.includes('.')
    ? value
    : `${value.slice(0, -1)}.000Z`;
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === canonicalValue;
}

function isFreshnessStampStatus(value: unknown): value is FreshnessStampStatus {
  return typeof value === 'string' && FRESHNESS_STAMP_STATUS_SET.has(value);
}

function isFreshnessStampSectionPropsV1(
  value: unknown,
): value is FreshnessStampSectionPropsV1 {
  return (
    isRecord(value) &&
    hasExactKeys(value, FRESHNESS_STAMP_SECTION_KEYS) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.title) &&
    isNullablePublicTimestamp(value.asOf) &&
    isFreshnessStampStatus(value.status) &&
    typeof value.maxAgeHours === 'number' &&
    Number.isFinite(value.maxAgeHours) &&
    value.maxAgeHours > 0
  );
}

/**
 * Runtime admission predicate for the public FreshnessStamp V1 contract.
 * The API accepts only the public stamp vocabulary and public UTC timestamp
 * shape, including a review pack with no aggregate as-of. It rejects incomplete
 * section rows and undeclared fields before the workspace can conceal a
 * governed source row by shortening the table.
 */
export function isFreshnessStampPropsV1(
  value: unknown,
): value is FreshnessStampPropsV1 {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, FRESHNESS_STAMP_PROP_KEYS) ||
    !Object.prototype.hasOwnProperty.call(value, 'asOf') ||
    !isNullablePublicTimestamp(value.asOf)
  ) {
    return false;
  }

  if (
    (Object.prototype.hasOwnProperty.call(value, 'label') &&
      !isNonEmptyString(value.label)) ||
    (Object.prototype.hasOwnProperty.call(value, 'status') &&
      !isFreshnessStampStatus(value.status))
  ) {
    return false;
  }

  if (Object.prototype.hasOwnProperty.call(value, 'sections')) {
    if (!Array.isArray(value.sections) || !value.sections.every(isFreshnessStampSectionPropsV1)) {
      return false;
    }
  }

  return (
    isNonEmptyString(value.asOf) ||
    Object.prototype.hasOwnProperty.call(value, 'status') ||
    (Array.isArray(value.sections) && value.sections.length > 0)
  );
}
